import { useTranslation } from "react-i18next";

import { AnserTip } from "@/components/ui/anser-tip";
import type { AnserTip as AnserTipData } from "./anser-tip";
import type { RailStarter } from "./rail-starter";

interface Props {
  /** Typed tips from `useAnserTips` or a page override; take precedence. */
  tips?: AnserTipData[];
  /** Legacy starters; shown as dialogue tips when no `tips` are given. */
  starters?: RailStarter[];
  /** The two quick-question chips under the tips. */
  quickQuestions?: string[];
  /** Prefill the composer with the prompt (user still sends it). */
  onPick: (prompt: string) => void;
}

function startersAsTips(starters: RailStarter[]): AnserTipData[] {
  return starters.map((starter) => ({
    id: starter.id,
    kind: "dialogue",
    text: starter.title,
    cta: null,
    prompt: starter.prompt,
    ai: false,
  }));
}

/**
 * The empty-panel state (GRA-149): up to three typed tip cards drawn from
 * what the business's data says, then two quick-question chips. Cards and
 * chips prefill the composer rather than sending. Tokens only.
 */
export function RailStarterList({
  tips,
  starters = [],
  quickQuestions = [],
  onPick,
}: Props) {
  const { t } = useTranslation("intelligence");
  const list = tips ?? startersAsTips(starters);

  return (
    <div className="flex flex-col gap-2.5" data-testid="anser-tips">
      {list.length > 0 ? (
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-g-ink-400">
          {t("rail.starters.heading")}
        </div>
      ) : null}
      {list.map((tip) => (
        <AnserTip
          key={tip.id}
          kind={tip.kind}
          text={tip.text}
          ai={tip.ai}
          cta={tip.cta}
          prompt={tip.prompt}
          onPick={onPick}
        />
      ))}
      {quickQuestions.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {quickQuestions.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => onPick(question)}
              className="rounded-g-pill bg-g-primary-tint px-[11px] py-[7px] text-[12px] font-semibold text-g-primary-press transition-colors duration-150 hover:bg-g-primary-wash focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring"
            >
              {question}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
